import { useCallback, useMemo } from 'react'
import useUserStore from '@/user/userStore'
import { useCreateUser } from '@/user/userQueries'
import { AboutYouFormValues } from './rules/aboutYouSchema'
import { extractCountryCodeAndMobile } from '@/user/mobile/MobileUtils'
import { AddressDetails } from '@/common/types'

interface UseAboutYouLogicResult {
    data: AboutYouFormValues | undefined
    loading: boolean
    saving: boolean
    isSuccess: boolean
    onSubmit: (data: AboutYouFormValues) => void
}

export function useAboutYouLogic(): UseAboutYouLogicResult {
    const { user, setUser } = useUserStore()
    const createUser = useCreateUser()

    const data = useMemo(() => {
        if (!user) return undefined

        const { countryCode, mobile } = extractCountryCodeAndMobile(user.mobile)
        const defaultAddress = {
            addressPrefix: '',
            streetNumber: '',
            streetName: '',
            streetType: '',
            city: '',
            state: '',
            postCode: '',
            country: '',
        }

        return {
            salutation: user.salutation || '',
            firstName: user.firstName || '',
            middleName: user.middleName || '',
            lastName: user.lastName || '',
            dateOfBirth: user.dateOfBirth || '',
            differentPostalAddress: false,
            residential: (user.addresses?.residentialAddress || defaultAddress) as AddressDetails,
            postal: (user.addresses?.postalAddress || defaultAddress) as AddressDetails,
            countryCode,
            mobile,
        }
    }, [user])

    const onSubmit = useCallback(
        (formData: AboutYouFormValues) => {
            const mobile = `${formData.countryCode}${formData.mobile}`
            const addresses = {
                residentialAddress: formData.residential,
                postalAddress: (formData.differentPostalAddress
                    ? formData.postal
                    : formData.residential) as AddressDetails,
            }

            createUser.mutate({
                salutation: formData.salutation,
                firstName: formData.firstName,
                middleName: formData.middleName,
                lastName: formData.lastName,
                dateOfBirth: formData.dateOfBirth,
                mobile,
                addresses,
            })

            if (user) {
                setUser({
                    ...user,
                    ...formData,
                    mobile,
                    addresses,
                })
            }
        },
        [createUser, user, setUser],
    )

    return {
        data,
        loading: false,
        saving: createUser.isPending,
        isSuccess: createUser.isSuccess,
        onSubmit,
    }
}
